import { Currency } from '@seapoint/server/enums';
import { EuroIcon, Info, PercentIcon, PoundSterlingIcon, TrendingDown, TrendingUp } from 'lucide-react';
import { ReactNode } from 'react';
import { AvatarNameDescription } from '../../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { Tooltip } from '../../../../design-system/tooltip/tooltip';
import { cn } from '../../../../utils/cn';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';

const renderTrendIcon = (trend: Comparison['trend'], trendValueClass: Comparison['trendValueClass']) => {
  switch (trend) {
    case 'up':
      return <TrendingUp className={cn('h-6 w-6', trendValueClass)} />;
    case 'down':
      return <TrendingDown className={cn('h-6 w-6', trendValueClass)} />;
    case 'neutral':
      return <span className={cn('text-xs', trendValueClass)}>No change</span>;
    default: {
      exhaustiveCheck(trend);
    }
  }
};

export type TopVendor = {
  id: string;
  name: string;
  avatarUrl?: string | null;
  amount: number;
  percent: number;
};

export type Comparison = {
  value: string;
  percent: string;
  trend: 'up' | 'down' | 'neutral';
  trendValueClass: 'text-seapoint-green' | 'text-seapoint-red' | 'text-seapoint-gray-text';
  tooltipText: string;
};

type TopInOutVendorListProps = {
  title: string;
  currency: Currency;
  vendors: TopVendor[];
  footerAction:
    | {
        visible: false;
      }
    | {
        visible: true;
        icon: ReactNode;
        label: string;
        onClick: () => void;
      };
  view: 'percent' | 'amount';
  changeView: (view: 'percent' | 'amount') => void;
  className?: string;
  /** Message shown when there are no vendors in the selected timeframe */
  emptyText?: string;
  comparison?: Comparison | null;
  numberOfVendors: number;
};

export const TopInOutVendorList = ({
  title,
  currency,
  vendors,
  footerAction,
  view,
  comparison,
  changeView,
  className,
  emptyText,
  numberOfVendors,
}: TopInOutVendorListProps) => {
  const visibleVendors = vendors.slice(0, numberOfVendors);
  return (
    <Stack
      space={3}
      className={cn(className, 'h-full w-full rounded-xl border-[0.5px] border-seapoint-gray-border py-6')}
    >
      <Inline justify="between" align="center" className="w-full px-7">
        <h3 className="text-base font-semibold text-black">{title}</h3>
        <Inline space={0}>
          <Button
            variant="ghost"
            className="px-2"
            onClick={() => changeView('amount')}
            aria-label={`View amount in ${currency}`}
          >
            {currency === Currency.GBP ? (
              <PoundSterlingIcon
                className={cn('h-4 w-4', {
                  'text-black': view === 'amount',
                  'text-seapoint-gray-text': view === 'percent',
                })}
              />
            ) : (
              <EuroIcon
                className={cn('h-4 w-4', {
                  'text-black': view === 'amount',
                  'text-seapoint-gray-text': view === 'percent',
                })}
              />
            )}
          </Button>
          <Button
            variant="ghost"
            className="px-2"
            onClick={() => changeView('percent')}
            aria-label={`View values percentage`}
          >
            <PercentIcon
              className={cn('h-4 w-4', {
                'text-black': view === 'percent',
                'text-seapoint-gray-text': view === 'amount',
              })}
            />
          </Button>
        </Inline>
      </Inline>

      <p className="px-7 text-xs font-normal text-seapoint-gray-text">Top Sources</p>
      <div className="w-full flex-1 px-7">
        {vendors.length === 0 ? (
          <div className="bg-seapoint-gray-faint/30 flex min-h-[120px] w-full items-center justify-center rounded-lg">
            <p className="text-sm text-seapoint-gray-text">{emptyText ?? 'No results for this period'}</p>
          </div>
        ) : (
          <Stack as="ul" role="list" space={2} className="w-full flex-1">
            {visibleVendors.map((v) => (
              <li key={v.id} className="w-full py-2">
                <Inline justify="between" align="center" className="w-full">
                  <AvatarNameDescription
                    className="min-w-0 flex-1"
                    name={v.name}
                    avatar={{ showAvatar: true, avatarUrl: v.avatarUrl, size: 'sm' }}
                  />

                  <span className="text-sm font-normal text-black">
                    {view === 'amount'
                      ? getAmountWithCurrency({ amount: v.amount, currency, isAccountBalance: true })
                      : (() => {
                          const epsilon = 0.1; // display threshold at 0.1%
                          if (v.percent > 0 && v.percent < epsilon) return `<${epsilon}%`;
                          return `${v.percent.toFixed(1)}%`;
                        })()}
                  </span>
                </Inline>
              </li>
            ))}
          </Stack>
        )}
      </div>

      {footerAction.visible && (
        <Button
          variant="link"
          iconRight={footerAction.icon}
          onClick={footerAction.onClick}
          className="mx-7 w-full justify-start rounded-3xl px-0 text-sm font-medium text-seapoint-blue-link"
        >
          {footerAction.label}
        </Button>
      )}
      {comparison && (
        <Inline space={2} className="w-full border-t-[0.5px] border-seapoint-gray-border px-7 pt-2" justify="between">
          <Stack space={2} className="mt-4">
            <p className="text-xs text-seapoint-gray-text">Total</p>
            <p>{comparison.value}</p>
          </Stack>
          <Stack space={2} align="end">
            {renderTrendIcon(comparison.trend, comparison.trendValueClass)}
            {comparison.trend !== 'neutral' && (
              <Inline space={1} align="center">
                <p className="text-xs text-seapoint-gray-text">
                  {`${comparison.trend === 'up' ? 'Up' : 'Down'} ${comparison.percent}`}
                </p>
                <Tooltip
                  contentAlign="center"
                  content={comparison.tooltipText}
                  trigger={<Info size={12} className="text-seapoint-gray-text" />}
                />
              </Inline>
            )}
          </Stack>
        </Inline>
      )}
    </Stack>
  );
};

export default TopInOutVendorList;
